import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Snackbar,
  useMediaQuery,
  useTheme,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slide,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { useMaterialUIController } from "context";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import MDButton from "components/MDButton";
import { application } from "globals/endpoints";
import loggerFactory from "globals/logger/logger-factory";
import CTLNotification from "application/components/CTLNotification";

export default function SendEmailDialog({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("predefined");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedGreeting, setSelectedGreeting] = useState("");
  const [controller] = useMaterialUIController();
  const [messageType, setMessageType] = useState([]);
  const { darkMode } = controller;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));



  useEffect(() => {
    application
      .post("predefinedMessage/listAll/Email")
      .then((response) => {
        setMessageType(response.data.data);
        loggerFactory.debug(
          "Email Send",
          "Successfully get predefine Message list"
        );
      })
      .catch((error) => {
        setMessageType([]);
        loggerFactory.error(
          "Email Send",
          "Error fetching message types:",
          error
        );
      });
  }, [open]);

  const handleChange = (e) => {
    setSelectedGreeting(e.target.value);
    setMessage(e.target.value?.messageContent || "");
  };

  const handleClose = () => {
    onClose();
    setEmail("");
    setCc("");
    setMessage("");
    setSelectedGreeting("");
    setSubject("");
    setMode("predefined");
  };

  const handleSubmit = async () => {
    setLoading(true);
    await application
      .post("messagingAgent/sendMessage", {
        toUser: email,
        emailCc: cc,
        subject: subject,
        message: message,
        messageType: "EMAIL",
      })
      .then((response) => {
        setNotify({
          isOpen: true,
          status: response.data.message,
          type: "success",
          message: response.data.messageDetail,
          pagename: "Email",
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          status: error.response.data.message,
          message: error.response.data.messageDetail,
          type: "error",
          pagename: "Email",
        });
      });
    setLoading(false);
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        // fullScreen={fullScreen}
        fullScreen
        fullWidth
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
      >
        <DialogTitle
          sx={{
            ...CTLDialogContent(theme, { darkMode }),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Send Email
          </Typography>
          <IconButton onClick={onClose} size="medium" color="error">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <Box
            sx={{
              pt: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextField
              label="To"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              variant="outlined"
              autoFocus
            />
            <TextField
              label="Cc"
              type="email"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              variant="outlined"
            />

            {/* Radio Button Section */}
            <RadioGroup
              row
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setMessage("");
                setSelectedGreeting("");
              }}
            >
              <FormControlLabel
                value="predefined"
                control={<Radio />}
                label="Predefined Message"
              />
              <FormControlLabel
                value="custom"
                control={<Radio />}
                label="Custom Message"
              />
            </RadioGroup>

            {/* Slide Animations */}
            <Box
              sx={{
                minHeight: "200px",
                position: "relative",
                overflow: "hidden", // prevents scrollbars from showing
              }}
            >
              {/* Custom message panel */}
              <Slide
                direction="left"
                in={mode === "custom"}
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pt: 1,
                  }}
                >
                  <TextField
                    label="Message"
                    value={message}
                    placeholder="Type your message here..."
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    required
                    multiline
                    minRows={6}
                    maxRows={6}
                    variant="outlined"
                  />
                </Box>
              </Slide>

              {/* Predefined message panel */}
              <Slide
                direction="right"
                in={mode === "predefined"}
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    gap: 1,
                  }}
                >
                  <Select
                    value={selectedGreeting}
                    displayEmpty
                    onChange={(e) => handleChange(e)}
                    fullWidth
                    sx={{ mb: 1, height: "45px" }}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Message Type</MenuItem>
                    {messageType?.length > 1 ? (
                      messageType.map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item.messageType}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        No Predefined Message Available
                      </MenuItem>
                    )}
                  </Select>
                  <TextField
                    label="Message"
                    value={`${message}`}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={5}
                    maxRows={6}
                    variant="outlined"
                  />
                </Box>
              </Slide>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            ...CTLDialogContent(theme, { darkMode }),
            pb: 2,
          }}
        >
          <MDButton onClick={handleClose} color="secondary" variant="contained">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={!email || !message || loading}
          >
            {loading ? "Sending..." : "Send Email"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}
